import type { SortingState } from '@tanstack/react-table';

/**
 * Pure (URL string) ⇆ (state) translation for the Quest Completion Events page.
 * Filters, sorting, the current page, and the open detail modal are all carried
 * as flat, human-readable query params so an events view can be deep-linked and
 * shared. Defaults are omitted from the URL to keep it clean.
 */

export interface QuestEventsFilters {
  seasonId: string;
  roundId: string;
  roundAppId: string;
  questId: string;
  // 'all' | 'Easy' | 'Deep'
  tier: string;
  // 'awarded' | 'all'
  completionView: string;
  developerCredentialId: string;
  search: string;
}

export interface QuestEventsUrlState {
  filters: QuestEventsFilters;
  sorting: SortingState;
  page: number;
  selectedEventId: string | null;
}

/** Every query-param key this page owns. Foreign params are preserved. */
export const QUEST_EVENTS_URL_KEYS = [
  'season',
  'round',
  'app',
  'quest',
  'tier',
  'view',
  'credential',
  'search',
  'sort',
  'page',
  'event',
] as const;

const DEFAULT_FILTERS: QuestEventsFilters = {
  seasonId: '',
  roundId: '',
  roundAppId: '',
  questId: '',
  tier: 'all',
  completionView: 'awarded',
  developerCredentialId: '',
  search: '',
};

const DEFAULT_SORTING: SortingState = [{ id: 'completedAt', desc: true }];

const sortableColumns = new Set(['completedAt', 'created', 'walletId']);
const tierValues = new Set(['all', 'Easy', 'Deep']);
const completionViewValues = new Set(['awarded', 'all']);

function parsePage(value: string | null): number {
  if (!value) {
    return 1;
  }
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return 1;
  }
  return parsed;
}

function parseSorting(value: string | null): SortingState {
  if (!value) {
    return DEFAULT_SORTING;
  }
  const [id, direction = 'asc'] = value.split(':');
  if (!id || !sortableColumns.has(id)) {
    return DEFAULT_SORTING;
  }
  return [{ id, desc: direction === 'desc' }];
}

export function parseQuestEventsUrlState(
  params: URLSearchParams,
): QuestEventsUrlState {
  const tier = params.get('tier');
  const completionView = params.get('view');
  return {
    filters: {
      seasonId: params.get('season') ?? '',
      roundId: params.get('round') ?? '',
      roundAppId: params.get('app') ?? '',
      questId: params.get('quest') ?? '',
      tier: tier && tierValues.has(tier) ? tier : 'all',
      completionView:
        completionView && completionViewValues.has(completionView)
          ? completionView
          : 'awarded',
      developerCredentialId: params.get('credential') ?? '',
      search: params.get('search') ?? '',
    },
    sorting: parseSorting(params.get('sort')),
    page: parsePage(params.get('page')),
    selectedEventId: params.get('event'),
  };
}

export function buildQuestEventsParams(
  state: QuestEventsUrlState,
): Record<string, string> {
  const params: Record<string, string> = {};
  const { filters } = state;

  if (filters.seasonId) {
    params.season = filters.seasonId;
  }
  if (filters.roundId) {
    params.round = filters.roundId;
  }
  if (filters.roundAppId) {
    params.app = filters.roundAppId;
  }
  if (filters.questId) {
    params.quest = filters.questId;
  }
  if (filters.tier && filters.tier !== DEFAULT_FILTERS.tier) {
    params.tier = filters.tier;
  }
  if (
    filters.completionView &&
    filters.completionView !== DEFAULT_FILTERS.completionView
  ) {
    params.view = filters.completionView;
  }
  if (filters.developerCredentialId) {
    params.credential = filters.developerCredentialId;
  }
  if (filters.search) {
    params.search = filters.search;
  }

  const sort = state.sorting[0];
  if (
    sort &&
    (sort.id !== DEFAULT_SORTING[0]?.id ||
      sort.desc !== DEFAULT_SORTING[0]?.desc)
  ) {
    params.sort = `${sort.id}:${sort.desc ? 'desc' : 'asc'}`;
  }

  if (state.page > 1) {
    params.page = String(state.page);
  }
  if (state.selectedEventId) {
    params.event = state.selectedEventId;
  }

  return params;
}
